import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';

import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';

import ActivityPlanningFilterBar from './ActivityPlanningFilterBar';
import ActivityTreePanel from './ActivityTreePanel';
import ActivityTaskBudgetPanel from './ActivityTaskBudgetPanel';
import ActivityFormPanel from './ActivityFormPanel';
import BudgetImpactPanel from './BudgetImpactPanel';
import TemplateLibraryPanel from './TemplateLibraryPanel';
import KpiPlanGridPanel from './KpiPlanGridPanel';
import ActivityPlanningCostCenterSelector from './ActivityPlanningCostCenterSelector';
import ActivityPlanningRegisterPanel from './ActivityPlanningRegisterPanel';
import CreateTaskDialog from './CreateTaskDialog';

import {
  ActivityCandidate,
  ActivityNode,
  ActivityPlanningCards,
  ActivityPlanningIndicatorScope,
  ActivityPlanningTreeScope,
  ActivityPlanningWorkbenchModel,
  ActivityTemplate,
  BudgetLineRow,
  TaskRow,
  IndicatorCandidate,
  KpiPlanRow,
} from './activityplanning.models';

import {
  fetchActivityPlanningAxes,
  fetchActivityPlanningGlobalObjectives,
  fetchActivityPlanningSpecificObjectives,
  fetchIndicatorScope,
  fetchActivityPlanningTree,
  addIndicator,
  saveIndicators,
  addActivity,
  applyTemplate,
  fetchActivityCandidates,
  fetchActivityPlanningWorkbench,
  fetchBudgetLines,
  fetchTemplates,
  saveBudgetLines,
  setPlanningCostCenter,
  validateActivityPlanning,
  fetchTaskRepository,
  savePlanningTasks,
  declareActivityReady,
  completeActivityPlanning,
  removeActivityPlan,
  saveActivity,
} from './activityplanning.apis';

import { fetchCombo } from '../core/coreapi';
import {
  normalizeKpiPlanRows,
  normalizePlanningTree,
  summaryCards,
} from './activityplanning.normalizers';
import { ComboRow } from '../core/coremodels';
import TaskPlanningDialog from './TaskPlanningDialog';

type State = {
  model?: ActivityPlanningWorkbenchModel;
  planningCycleId: number | null;
  budgetPeriodName: string;
  axisCode: string;
  globalObjectiveCode: string;
  specificObjectiveCode: string;
  axes: ComboRow[];
  globalObjectives: ComboRow[];
  specificObjectives: ComboRow[];
  accounts: ComboRow[];
  activities: ActivityNode[];
  selectedNode: ActivityNode | null;
  taskRows: TaskRow[];
  budgetRows: BudgetLineRow[];
  cards: ActivityPlanningCards | null;
  templates: ActivityTemplate[];
  indicatorCandidates: IndicatorCandidate[];
  indicatorScope: ActivityPlanningIndicatorScope;
  treeScope: ActivityPlanningTreeScope;
  kpiPlanRows: KpiPlanRow[];
  activityCandidates: ActivityCandidate[];
  selectedActivityCandidate: ActivityCandidate | null;
  driverValue: number;
  targetValue: number;
  activityWeight: number;
  activityDescription: string;
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;

  taskPlanningOpen: boolean;
  taskRepository: TaskRow[];
  taskRepositoryLoading: boolean;
  selectedRepositoryTask: TaskRow | null;
  createTaskOpen: boolean;
};

export default class ActivityPlanningWorkbench extends React.Component<
  PlanningPageProps,
  State
> {
  state: State = {
    model: undefined,
    planningCycleId:
      this.props.plancontext?.pci ?? this.props.planningContext?.pci ?? null,
    budgetPeriodName:
      this.props.plancontext?.bpn ?? this.props.planningContext?.bpn ?? '',
    axisCode: '',
    globalObjectiveCode: '',
    specificObjectiveCode: '',
    axes: [],
    globalObjectives: [],
    specificObjectives: [],
    accounts: [],
    activities: [],
    selectedNode: null,
    taskRows: [],
    budgetRows: [],
    cards: null,
    templates: [],
    indicatorCandidates: [],
    indicatorScope: { scope: 'OVERVIEW', canplan: false, count: 0, items: [] },
    treeScope: {
      scope: 'OVERVIEW',
      canplan: false,
      kpiplancount: 0,
      activityplancount: 0,
      taskplancount: 0,
      tree: [],
    },
    kpiPlanRows: [],
    activityCandidates: [],
    selectedActivityCandidate: null,
    driverValue: 0,
    targetValue: 0,
    activityWeight: 1,
    activityDescription: '',
    loading: false,
    saving: false,
    error: '',
    success: '',

    taskPlanningOpen: false,
    taskRepository: [],
    taskRepositoryLoading: false,
    selectedRepositoryTask: null,
    createTaskOpen: false,
  };

  componentDidMount() {
    void this.initialize();
  }

  componentDidUpdate(previous: PlanningPageProps) {
    const before =
      previous.plancontext?.pci ?? previous.planningContext?.pci ?? null;
    const current =
      this.props.plancontext?.pci ?? this.props.planningContext?.pci ?? null;
    if (Number(before || 0) !== Number(current || 0)) {
      this.setState(
        {
          planningCycleId: current,
          budgetPeriodName:
            this.props.plancontext?.bpn ??
            this.props.planningContext?.bpn ??
            '',
        },
        () => void this.initialize()
      );
    }
  }

  private pci = () =>
    Number(
      this.state.planningCycleId ??
        this.props.plancontext?.pci ??
        this.props.planningContext?.pci ??
        0
    );

  private initialize = async () => {
    if (!this.pci())
      return this.setState({ error: 'Planning Cycle context is missing.' });

    this.setState({ loading: true, error: '', success: '' });
    try {
      const [model, templates, axes, accounts] = await Promise.all([
        fetchActivityPlanningWorkbench(this.pci()),
        fetchTemplates().catch(() => []),
        fetchActivityPlanningAxes(this.pci()).catch(() => []),
        fetchCombo('chartofaccounts').catch(() => []),
      ]);

      this.setState({
        model,
        indicatorCandidates: [],
        indicatorScope: {
          scope: 'OVERVIEW',
          canplan: false,
          count: 0,
          items: [],
        },
        treeScope: {
          scope: 'OVERVIEW',
          canplan: false,
          kpiplancount: 0,
          activityplancount: 0,
          taskplancount: 0,
          tree: [],
        },
        // OVERVIEW keeps the governed Cost Centre's existing seasonal KPI Plans
        // visible. Strategic scope controls editing and the working tree, not
        // whether existing planning records can be reviewed.
        kpiPlanRows: normalizeKpiPlanRows(model),
        activities: [],
        templates,
        axes,
        accounts,
        axisCode: '',
        globalObjectiveCode: '',
        specificObjectiveCode: '',
        globalObjectives: [],
        specificObjectives: [],
        selectedNode: null,

        activityCandidates: [],
        selectedActivityCandidate: null,
        //taskRows: [],
        //budgetRows: [],
        ...this.emptyActivityContext(),
        cards: summaryCards(model, null),
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private loadStrategicScope = async (
    axisCode: string,
    globalObjectiveCode = '',
    specificObjectiveCode = ''
  ) => {
    if (!axisCode) {
      this.setState({
        indicatorCandidates: [],
        indicatorScope: {
          scope: 'OVERVIEW',
          canplan: false,
          count: 0,
          items: [],
        },
        treeScope: {
          scope: 'OVERVIEW',
          canplan: false,
          kpiplancount: 0,
          activityplancount: 0,
          taskplancount: 0,
          tree: [],
        },
        // Clearing strategic scope returns the KPI grid to the Cost Centre
        // overview population while keeping the AP-014 working tree empty.
        kpiPlanRows: this.state.model
          ? normalizeKpiPlanRows(this.state.model)
          : [],
        activities: [],
        selectedNode: null,
        //taskRows: [],
        //budgetRows: [],
        ...this.emptyActivityContext(),
      });
      return;
    }

    const [indicatorScope, treeScope] = await Promise.all([
      fetchIndicatorScope(
        this.pci(),
        axisCode,
        globalObjectiveCode,
        specificObjectiveCode
      ),
      fetchActivityPlanningTree(
        this.pci(),
        axisCode,
        globalObjectiveCode,
        specificObjectiveCode
      ),
    ]);

    const scopedModel = { tree: treeScope.tree };
    this.setState({
      indicatorScope,
      treeScope,
      indicatorCandidates: indicatorScope.canplan
        ? indicatorScope.items.filter(
            (item: IndicatorCandidate) => !item.planned
          )
        : [],
      kpiPlanRows: normalizeKpiPlanRows(scopedModel),
      activities: normalizePlanningTree(
        treeScope.tree ?? [],
        this.state.model?.context
      ),
      selectedNode: null,
      activityCandidates: [],
      selectedActivityCandidate: null,
      //taskRows: [],
      //budgetRows: [],
      ...this.emptyActivityContext(),
    });
  };

  private reload = async (_selected?: ActivityNode | null) => {
    const model = await fetchActivityPlanningWorkbench(this.pci());
    this.setState({ model, cards: summaryCards(model, null) });
    await this.loadStrategicScope(
      this.state.axisCode,
      this.state.globalObjectiveCode,
      this.state.specificObjectiveCode
    );
  };

  private refreshSelectedActivity = async (activity: ActivityNode) => {
    const { axisCode, globalObjectiveCode, specificObjectiveCode } = this.state;

    const [model, indicatorScope, treeScope] = await Promise.all([
      fetchActivityPlanningWorkbench(this.pci()),

      fetchIndicatorScope(
        this.pci(),
        axisCode,
        globalObjectiveCode,
        specificObjectiveCode
      ),

      fetchActivityPlanningTree(
        this.pci(),
        axisCode,
        globalObjectiveCode,
        specificObjectiveCode
      ),
    ]);

    const activities = normalizePlanningTree(
      treeScope.tree ?? [],
      model.context
    );

    /*
     * Recover the Activity from the NEW tree.
     * Do not retain the stale ActivityNode object.
     */
    const refreshedActivity =
      activities.find(
        (x) =>
          x.nodetype === 'ACTIVITY' &&
          Number(x.activityplanid) === Number(activity.activityplanid)
      ) ?? null;

    this.setState(
      {
        model,
        cards: summaryCards(model, null),

        indicatorScope,
        treeScope,

        indicatorCandidates: indicatorScope.canplan
          ? indicatorScope.items.filter((x: IndicatorCandidate) => !x.planned)
          : [],

        kpiPlanRows: normalizeKpiPlanRows({
          tree: treeScope.tree,
        }),

        activities,

        selectedNode: refreshedActivity,

        taskRows: [],
        budgetRows: [],
        taskRepository: [],
        selectedRepositoryTask: null,
      },
      () => {
        if (refreshedActivity) {
          void this.loadActivity(refreshedActivity, model);
        }
      }
    );
  };

  private canEditPlanning = () => {
    return Boolean(this.state.model?.actions?.canEdit);
  };

  private canPlanCurrentScope = () => {
    return Boolean(this.state.treeScope.canplan && this.canEditPlanning());
  };

  private declareReady = async (row: any) => {
    const activityplanid = Number(row?.activityplanid ?? 0);

    if (!activityplanid) {
      this.setState({
        error: 'Activity Plan identity is unavailable.',
      });
      return;
    }

    if (!row?.canready) {
      this.setState({
        error: 'This Activity cannot currently be declared Ready.',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      await declareActivityReady(activityplanid);

      await this.reload();

      this.setState({
        success: 'Activity declared Ready.',
      });
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({
        saving: false,
      });
    }
  };

  private completePlanning = async () => {
    const model = this.state.model;

    if (!model) return;

    if (!model.actions.canComplete) {
      this.setState({
        error:
          'All Activities must be Ready and technically valid before Planning can be completed.',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      await completeActivityPlanning(this.pci(), Number(model.context.cci));

      /*
       * Full authoritative reload is intentional.
       * Completion changes the CC governance boundary and
       * therefore affects every mutation control.
       */
      await this.initialize();

      this.setState({
        success: 'Cost Centre Activity Planning completed successfully.',
      });
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({
        saving: false,
      });
    }
  };

  private emptyActivityContext = () => ({
    taskRows: [] as TaskRow[],
    budgetRows: [] as BudgetLineRow[],
    taskRepository: [] as TaskRow[],
    selectedRepositoryTask: null as TaskRow | null,
    taskRepositoryLoading: false,
    //taskPlanningOpen: false,
    //createTaskOpen: false,
  });

  // private loadActivity = async (
  //   activity: ActivityNode,
  //   model = this.state.model
  // ) => {
  //   if (!model) return;

  //   const tree: ActivityNode[] = normalizePlanningTree(
  //     this.state.treeScope?.tree ?? [],
  //     model.context
  //   );

  //   const taskRows: TaskRow[] = tree
  //     .filter(
  //       (x) =>
  //         x.nodetype === 'TASK' &&
  //         Number(x.activityplanid) === Number(activity.activityplanid)
  //     )
  //     .map((x) => ({
  //       id: x.taskplanid ?? x.rid,
  //       rid: x.taskplanid ?? x.rid,
  //       taskplanid: x.taskplanid ?? x.rid,
  //       taskid: x.taskid ?? null,
  //       activityplanid: x.activityplanid ?? null,

  //       code: x.code,
  //       name: x.name,
  //       description: x.description ?? '',

  //       weight: Number(x.raw?.weight ?? x.raw?.taskweight ?? 1),

  //       status: x.status ?? '',

  //       plannedamount: Number(x.totalamount ?? 0),
  //       budgetlinecount: Number(x.budgetlinecount ?? 0),
  //       budgetstate: Number(x.budgetstate ?? 0),

  //       _state: 'clean',
  //       raw: x.raw,
  //     }));

  //   // const collections = await Promise.all(
  //   //   taskRows.map((x) =>
  //   //     fetchBudgetLines(Number(x.taskplanid)).catch(() => [])
  //   //   )
  //   // );

  //   this.setState({
  //     taskRows,
  //     budgetRows: [], //collections.flat(),
  //     cards: summaryCards(model, null),
  //   });

  //   await this.loadTaskRepository(activity);
  // };

  private loadActivity = async (
    activity: ActivityNode,
    model = this.state.model
  ) => {
    if (!model) return;

    const tree: ActivityNode[] = normalizePlanningTree(
      this.state.treeScope?.tree ?? [],
      model.context
    );

    /*
     * Resolve all seasonal TaskPlans belonging to
     * the selected ActivityPlan.
     */
    const taskRows: TaskRow[] = tree
      .filter(
        (x) =>
          x.nodetype === 'TASK' &&
          Number(x.activityplanid) === Number(activity.activityplanid)
      )
      .map((x) => ({
        id: x.taskplanid ?? x.rid,

        rid: x.taskplanid ?? x.rid,

        taskplanid: x.taskplanid ?? x.rid,

        taskid: x.taskid ?? null,

        activityplanid: x.activityplanid ?? null,

        code: x.code,

        name: x.name,

        description: x.description ?? '',

        weight: Number(x.raw?.weight ?? x.raw?.taskweight ?? 1),

        status: x.status ?? '',

        plannedamount: Number(x.totalamount ?? 0),

        budgetlinecount: Number(x.budgetlinecount ?? 0),

        budgetstate: Number(x.budgetstate ?? 0),

        _state: 'clean',

        raw: x.raw,
      }));

    /*
     * AP-091:
     *
     * The read-only Activity Task/Budget panel receives
     * the complete BudgetLine collection for all seasonal
     * TaskPlans under the selected ActivityPlan.
     *
     * ActivityTaskBudgetPanel then filters this collection
     * locally by the selected taskplanid.
     */
    const collections = await Promise.all(
      taskRows
        .filter((x) => Number(x.taskplanid ?? 0) > 0)
        .map((x) =>
          fetchBudgetLines(Number(x.taskplanid)).catch(
            () => [] as BudgetLineRow[]
          )
        )
    );

    const budgetRows: BudgetLineRow[] = collections.flat();

    this.setState({
      taskRows,
      budgetRows,

      cards: summaryCards(model, null),
    });

    /*
     * Repository is independent from the seasonal
     * TaskPlan/BudgetLine collection.
     */
    await this.loadTaskRepository(activity);
  };

  private selectNode = async (node: ActivityNode) => {
    this.setState({
      selectedNode: node,
      activityCandidates: [],
      selectedActivityCandidate: null,
      //taskRows: [],
      //budgetRows: [],
      cards: summaryCards(this.state.model, null),
      error: '',
      success: '',
      ...this.emptyActivityContext(),
    });
    try {
      if (node.nodetype === 'KPI' && this.state.treeScope.canplan) {
        const kpiplanid = Number(node.kpiplanid || 0);
        if (kpiplanid <= 0) return;
        const activityCandidates = await fetchActivityCandidates(
          kpiplanid,
          this.pci(),
          this.state.model?.context.ccc || ''
        ).catch(() => []);
        this.setState({ activityCandidates });
      } else if (node.nodetype === 'ACTIVITY') {
        await this.loadActivity(node);
      } else if (node.nodetype === 'TASK') {
        const budgetRows = await fetchBudgetLines(
          Number(node.taskplanid || node.rid)
        );
        const taskRows: TaskRow[] = [
          {
            id: node.taskplanid ?? node.rid,
            rid: node.taskplanid ?? node.rid,
            taskplanid: node.taskplanid ?? node.rid,
            taskid: node.taskid ?? null,
            activityplanid: node.activityplanid ?? null,
            code: node.code,
            name: node.name,
            description: node.description ?? '',
            weight: Number(node.raw?.weight ?? 1),
            status: node.status ?? '',
            plannedamount: Number(node.totalamount ?? 0),
            budgetlinecount: Number(node.budgetlinecount ?? 0),
            budgetstate: Number(node.budgetstate ?? 0),
            _state: 'clean',
            raw: node.raw,
          },
        ];
        this.setState({ taskRows, budgetRows });
      }
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private selectedActivityPlan = () => {
    const node = this.state.selectedNode;

    if (!node) return null;

    if (String(node.nodetype || '').toUpperCase() !== 'ACTIVITY') return null;

    return node;
  };

  private changeCostCenter = async (costcenterid: number) => {
    const stamp = this.state.model?.context.profilestamp;
    if (!stamp)
      return this.setState({
        error: 'Planning Context stamp is unavailable. Refresh and retry.',
      });
    this.setState({ saving: true, error: '', success: '' });
    try {
      await setPlanningCostCenter(this.pci(), costcenterid, stamp);
      await this.initialize();
      this.setState({ success: 'Working Cost Center changed.' });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  private addActivity = async () => {
    if (!this.canEditPlanning()) {
      return this.setState({
        error: 'Activity Planning is read-only for this Cost Centre.',
      });
    }

    if (!this.state.treeScope.canplan) {
      return this.setState({
        error: 'Select a Specific Objective (OS) before planning Activities.',
      });
    }

    const node = this.state.selectedNode;
    const candidate = this.state.selectedActivityCandidate;
    if (node?.nodetype !== 'KPI')
      return this.setState({
        error: 'Select an Indicator before adding an Activity.',
      });
    if (!candidate) return this.setState({ error: 'Select an Activity.' });
    const kpiplanid = Number(node.kpiplanid || 0);
    if (kpiplanid <= 0)
      return this.setState({
        error: 'The selected Indicator has no seasonal KPI Plan.',
      });
    this.setState({ saving: true, error: '', success: '' });
    try {
      await addActivity({
        kpiplanid,
        activityid: candidate.rid,
        planningcycleid: this.pci(),
        weight: this.state.activityWeight,
        drivervalue: this.state.driverValue,
        targetvalue: this.state.targetValue,
        description: this.state.activityDescription,
      });
      this.setState({
        selectedActivityCandidate: null,
        driverValue: 0,
        targetValue: 0,
        activityWeight: 1,
        activityDescription: '',
        success: 'Activity added successfully.',
      });
      await this.reload();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  private updateActivity = async () => {
    const {
      selectedNode,
      activityWeight,
      driverValue,
      targetValue,
      activityDescription,
    } = this.state;

    const activityplanid = Number(
      selectedNode?.activityplanid ?? 0
    );

    if (!activityplanid) {
      this.props.showNotification?.(
        'No Activity Plan is selected.',
        'error'
      );
      return;
    }

    if (!activityDescription?.trim()) {
      this.props.showNotification?.(
        'Expected Result / Planning Notes is required.',
        'error'
      );
      return;
    }

    this.setState({ saving: true });

    try {
      const result = await saveActivity({
        activityplanid,
        weight: Number(activityWeight ?? 0),
        drivervalue: Number(driverValue ?? 0),
        targetvalue: Number(targetValue ?? 0),
        description: activityDescription.trim(),
    });

      /*
       * If your service wrapper already throws for rc != 0,
       * this check isn't required.
       */
      if (result?.rc && result.rc !== 0) {
        throw new Error(
          result?.em ??
          'Unable to update Activity Plan.'
        );
      }

      await this.reload();

      this.props.showNotification?.(
        result?.sd?.changed === false
          ? 'No Activity changes were detected.'
          : 'Activity Plan updated successfully.',
        'success',
      );
    }
    catch (error: any) {
      this.props.showNotification?.(
        'error',
        error?.message ??
        'Unable to update Activity Plan.'
      );
    }
    finally {
      this.setState({ saving: false });
    }
  };

  private saveBudgetRows = async (rows: any[]) => {
    const dirty = (rows || []).filter((x) => x._state && x._state !== 'clean');
    if (!dirty.length) return;
    const taskplanid = Number(
      dirty[0]?.taskplanid || dirty[0]?.raw?.taskplanid || 0
    );
    if (!taskplanid)
      throw new Error('Select a Task before saving budget lines.');
    this.setState({ saving: true, error: '', success: '' });
    try {
      await saveBudgetLines(taskplanid, dirty);
      this.setState({ success: 'Task budget saved.' });
      await this.reload(this.state.selectedNode);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
      throw error;
    } finally {
      this.setState({ saving: false });
    }
  };

  private addIndicator = async (payload: {
    candidate: IndicatorCandidate;
    baselinevalue: number;
    targetvalue: number;
    thresholdmin?: number | null;
    thresholdmax?: number | null;
    description: string;
  }) => {
    if (!this.canPlanCurrentScope()) {
      this.setState({
        error:
          'Creating Indicator is not allowed in the current planning state or strategic scope.',
      });
      return;
    }

    this.setState({ saving: true, error: '', success: '' });

    try {
      await addIndicator({
        kpiid: payload.candidate.kpiid,
        planningcycleid: this.pci(),
        baselinevalue: payload.baselinevalue,
        targetvalue: payload.targetvalue,
        thresholdmin: payload.thresholdmin,
        thresholdmax: payload.thresholdmax,
        description: payload.description,
      });

      this.setState({
        success: 'Indicator added successfully.',
      });

      await this.reload();
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
      throw error;
    } finally {
      this.setState({ saving: false });
    }
  };

  private saveIndicators = async (rows: KpiPlanRow[]) => {
    if (!this.canPlanCurrentScope()) {
      this.setState({
        error:
          'Creating Indicator is not allowed in the current planning state or strategic scope.',
      });
      return;
    }

    this.setState({ saving: true, error: '', success: '' });

    try {
      await saveIndicators(rows);

      this.setState({
        success: 'Indicators saved.',
      });

      await this.reload(this.state.selectedNode);
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
      throw error;
    } finally {
      this.setState({ saving: false });
    }
  };

  private loadTaskRepository = async (activity: any) => {
    const activityplanid = Number(activity?.activityplanid ?? 0);

    if (!activityplanid) {
      this.setState({
        taskRepository: [],
        selectedRepositoryTask: null,
      });

      return;
    }

    this.setState({
      taskRepositoryLoading: true,
      selectedRepositoryTask: null,
    });

    try {
      const taskRepository = await fetchTaskRepository(activityplanid);

      this.setState({
        taskRepository,
      });
    } catch (error: any) {
      this.setState({
        taskRepository: [],
        error: error?.message ?? String(error),
      });
    } finally {
      this.setState({
        taskRepositoryLoading: false,
      });
    }
  };

  private addRepositoryTaskToPlan = async (task: TaskRow) => {
    const activity = this.selectedActivityPlan();
    if (!activity) return;

    const activityplanid = Number(activity?.activityplanid ?? 0);

    const taskid = Number(task.taskid ?? 0);

    if (!activityplanid || !taskid) return;

    this.setState({ saving: true, error: '' });

    try {
      await savePlanningTasks(activityplanid, [{ taskid }]);
      await this.loadActivity(activity);
      this.setState({ success: 'Task added to the Activity Plan.' });
    } catch (error: any) {
      this.setState({
        error: error?.message ?? String(error),
      });
    } finally {
      this.setState({
        saving: false,
      });
    }
  };

  // private openTaskPlanning = () => {
  //   const activity = this.selectedActivityPlan();

  //   if (!activity) return;

  //   this.setState({
  //     taskPlanningOpen: true,
  //     error: '',
  //     success: '',
  //   });
  // };

  private openTaskPlanning = () => {
    const activity = this.selectedActivityPlan();

    if (!activity) return;

    if (!this.canPlanCurrentScope()) {
      this.setState({
        error:
          'Task planning is not available in the current planning state or strategic scope.',
      });
      return;
    }

    this.setState({
      taskPlanningOpen: true,
      error: '',
      success: '',
    });
  };

  private closeTaskPlanning = () => {
    this.setState({
      taskPlanningOpen: false,
    });
  };

  private onTaskPlanningSaved = async () => {
    const activity = this.selectedActivityPlan();

    if (!activity) {
      return;
    }

    await this.refreshSelectedActivity(activity);
  };

  private openCreateTask = () => {
    if (!this.canEditPlanning()) {
      this.setState({
        error: 'Activity Planning is read-only for this Cost Centre.',
      });
      return;
    }

    const activity = this.selectedActivityPlan();

    if (!activity) {
      this.setState({
        error: 'Select an Activity before creating a Task.',
      });
      return;
    }

    if (!this.state.treeScope.canplan) {
      this.setState({
        error:
          'Task planning is only available within a planning-enabled Specific Objective.',
      });
      return;
    }

    this.setState({
      createTaskOpen: true,
      error: '',
      success: '',
    });
  };

  private closeCreateTask = () => {
    this.setState({
      createTaskOpen: false,
    });
  };

  private createTask = async (payload: {
    name: string;
    description: string;
    weight: number;
  }) => {
    const activity = this.selectedActivityPlan();

    if (!activity)
      throw new Error('The selected Activity is no longer available.');

    const activityplanid = Number(activity.activityplanid ?? 0);

    if (!activityplanid)
      throw new Error('The selected Activity has no seasonal Activity Plan.');

    const name = String(payload.name ?? '').trim();

    if (!name) throw new Error('Task Name is required.');

    const weight = Number(payload.weight ?? 1);

    if (!Number.isFinite(weight) || weight <= 0)
      throw new Error('Task Weight must be greater than zero.');

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      await savePlanningTasks(activityplanid, [
        {
          name,
          description: String(payload.description ?? '').trim(),
          weight,
        },
      ]);

      /*
       * Local refresh deliberately preserves the
       * selected Activity.
       *
       * This reloads:
       *   - seasonal TaskPlans
       *   - reusable Task repository
       *   - Activity financial presentation
       */
      //await this.loadActivity(activity);
      await this.refreshSelectedActivity(activity);

      this.setState({
        createTaskOpen: false,
        success: 'Task created and added to the Activity Plan.',
      });
    } catch (error: any) {
      this.setState({
        error: error?.message ?? String(error),
      });

      throw error;
    } finally {
      this.setState({
        saving: false,
      });
    }
  };

  private validate = async () => {
    this.setState({ saving: true, error: '', success: '' });
    try {
      await validateActivityPlanning(this.pci());
      this.setState({ success: 'Activity Planning structure is valid.' });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  private applyTemplate = async (templateCode: string) => {
    const node = this.state.selectedNode;
    if (node?.nodetype !== 'ACTIVITY')
      return this.setState({
        error: 'Select an Activity before applying a template.',
      });
    this.setState({ saving: true, error: '', success: '' });
    try {
      await applyTemplate(
        Number(node.activityplanid || node.rid),
        templateCode
      );
      this.setState({ success: 'Template applied successfully.' });
      await this.reload(node);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  private onAxisChange = async (axisCode: string) => {
    this.setState({
      axisCode,
      globalObjectiveCode: '',
      specificObjectiveCode: '',
      globalObjectives: [],
      specificObjectives: [],
      error: '',
    });
    try {
      const globalObjectives = axisCode
        ? await fetchActivityPlanningGlobalObjectives(this.pci(), axisCode)
        : [];
      this.setState({ globalObjectives });
      await this.loadStrategicScope(axisCode);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private onGlobalObjectiveChange = async (globalObjectiveCode: string) => {
    const axisCode = this.state.axisCode;
    this.setState({
      globalObjectiveCode,
      specificObjectiveCode: '',
      specificObjectives: [],
      error: '',
    });
    if (!axisCode) return;
    try {
      const specificObjectives = globalObjectiveCode
        ? await fetchActivityPlanningSpecificObjectives(
            this.pci(),
            axisCode,
            globalObjectiveCode
          )
        : [];
      this.setState({ specificObjectives });
      await this.loadStrategicScope(axisCode, globalObjectiveCode);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private onSpecificObjectiveChange = async (specificObjectiveCode: string) => {
    const { axisCode, globalObjectiveCode } = this.state;
    this.setState({ specificObjectiveCode, error: '' });
    if (!axisCode || !globalObjectiveCode) return;
    try {
      await this.loadStrategicScope(
        axisCode,
        globalObjectiveCode,
        specificObjectiveCode
      );
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private clearStrategicScope = async () => {
    this.setState({
      axisCode: '',
      globalObjectiveCode: '',
      specificObjectiveCode: '',
      globalObjectives: [],
      specificObjectives: [],
    });
    await this.loadStrategicScope('');
  };

  private removeActivityPlan = async (row: any) => {
    const activityplanid = Number(row.activityplanid ?? row.rid ?? 0);

    if (!activityplanid) {
      return;
    }

    const code =
      row.activitycode ??
      row.code ??
      row.shortcode ??
      '';

    const name =
      row.activityname ??
      row.name ??
      '';

    if (
      !window.confirm(
        `Remove ${code}${code && name ? ' — ' : ''}${name} from this plan?\n\n` +
        `Its seasonal Task Plans and Budget Lines will be retired. ` +
        `The reusable Activity definition will not be deleted.`
      )
    ) {
      return;
    }

    try {
      this.setState({
        loading: true,
      });

      await removeActivityPlan(activityplanid);

      /*
       * Reload the authoritative workbench.
       *
       * Do NOT manually splice register/tree rows.
       */
      await this.reload();

    } catch (error: any) {
      this.props.showNotification?.(
        error?.message || String(error),
        'error'
      );
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  render() {
    const model = this.state.model;
    const selected = this.state.selectedNode;
    const costCenters = (model?.context.costcenters || []).map((x) => ({
      rid: x.id,
      shc: x.code,
      nam: x.name,
    }));

    const selectedActivity = this.selectedActivityPlan();

    const planning = model?.planning;
    const canEditPlanning = Boolean(model?.actions?.canEdit);

    const canPlanCurrentScope = canEditPlanning && this.state.treeScope.canplan;

    const filters = (
      <ActivityPlanningFilterBar
        planningContext={
          this.props.plancontext ?? this.props.planningContext ?? null
        }
        onChangePlanningCycle={
          this.props.openPlanningCycleSwitcher ?? (() => undefined)
        }
        costCenterCode={model?.context.ccc ?? ''}
        axisCode={this.state.axisCode}
        globalObjectiveCode={this.state.globalObjectiveCode}
        specificObjectiveCode={this.state.specificObjectiveCode}
        scope={this.state.treeScope.scope}
        canplan={this.state.treeScope.canplan}
        costCenters={costCenters}
        axes={this.state.axes}
        globalObjectives={this.state.globalObjectives}
        specificObjectives={this.state.specificObjectives}
        onCostCenterChange={(code) => {
          const x = model?.context.costcenters.find((c) => c.code === code);
          if (x) void this.changeCostCenter(x.id);
        }}
        onAxisChange={this.onAxisChange}
        onGlobalObjectiveChange={this.onGlobalObjectiveChange}
        onSpecificObjectiveChange={this.onSpecificObjectiveChange}
        onRefresh={this.initialize}
        onClear={() => void this.clearStrategicScope()}
        loading={this.state.loading || this.state.saving}
      />
    );

    const actions = (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          variant="outlined"
          label={`Planning: ${planning?.statecode || '—'}`}
        />

        <Chip
          size="small"
          variant="outlined"
          label={`Ready ${planning?.readycount ?? 0} / ${
            planning?.activityplancount ?? 0
          }`}
        />

        <Chip
          size="small"
          variant="outlined"
          label={`Valid ${planning?.validcount ?? 0} / ${
            planning?.activityplancount ?? 0
          }`}
        />

        <Button variant="outlined" onClick={this.validate}>
          Validate
        </Button>

        {planning?.statecode === 'OPEN' && (
          <Button
            variant="contained"
            disabled={
              this.state.loading ||
              this.state.saving ||
              !model?.actions.canComplete
            }
            onClick={this.completePlanning}
          >
            Complete Planning
          </Button>
        )}
      </Stack>
    );

    return (
      <PlanningWorkbenchShell
        {...this.props}
        title={this.props.title || 'Activity Planning'}
        subtitle="Prepare Indicators, Activities, Tasks and task-level budgets for the selected Cost Center."
        icon={<LocalActivityOutlinedIcon />}
        loading={this.state.loading}
        saving={this.state.saving}
        error={this.state.error}
        success={this.state.success}
        filters={filters}
        // actions={
        //   <Button variant="outlined" onClick={this.validate}>
        //     Validate
        //   </Button>
        // }
        // actions={
        //   <Stack direction="row" spacing={1} alignItems="center">

        //     <Chip
        //       size="small"
        //       variant="outlined"
        //       label={`Planning: ${planning?.statecode || '—'
        //         }`}
        //     />

        //     <Chip
        //       size="small"
        //       variant="outlined"
        //       label={`Ready ${planning?.readycount ?? 0
        //         } / ${planning?.activityplancount ?? 0
        //         }`}
        //     />

        //     <Chip
        //       size="small"
        //       variant="outlined"
        //       label={`Valid ${planning?.validcount ?? 0
        //         } / ${planning?.activityplancount ?? 0
        //         }`}
        //     />

        //     <Button
        //       variant="outlined"
        //       onClick={this.validate}
        //     >
        //       Validate
        //     </Button>

        //     {planning?.statecode === 'OPEN' && (
        //       <Button
        //         variant="contained"
        //         disabled={
        //           this.state.loading ||
        //           this.state.saving ||
        //           !model?.actions.canComplete
        //         }
        //         onClick={this.completePlanning}
        //       >
        //         Complete Planning
        //       </Button>
        //     )}

        //   </Stack>
        // }
        actions={actions}
        onRefresh={this.initialize}
        headerExtension={
          model?.context.multiplecostcenters ? (
            <ActivityPlanningCostCenterSelector
              value={model.context.cci}
              options={model.context.costcenters}
              disabled={this.state.loading || this.state.saving}
              onChange={this.changeCostCenter}
            />
          ) : (
            <Chip
              size="small"
              label={`${model?.context.ccc ?? ''}${model?.context.ccn ? ` — ${model.context.ccn}` : ''}`}
            />
          )
        }
      >
        <Box sx={{ p: 3 }}>
          {(this.state.loading || this.state.saving) && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <CircularProgress size={22} />
              <Typography>
                {this.state.saving
                  ? 'Saving...'
                  : 'Loading Activity Planning...'}
              </Typography>
            </Stack>
          )}
          {model && !canEditPlanning && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Activity Planning for this Cost Centre is{' '}
              <strong>{model.planning.statecode}</strong>. Planning records are
              read-only.
            </Alert>
          )}
          <BudgetImpactPanel cards={this.state.cards} />
          {/* <ActivityPlanningRegisterPanel
            rows={model?.register ?? []}
            loading={this.state.loading || this.state.saving}
            onDeclareReady={this.declareReady}
          /> */}
          <ActivityPlanningRegisterPanel
            rows={model?.register}
            loading={this.state.loading}
            onPlanTasks={this.openTaskPlanning}
            onDeclareReady={this.declareReady}
            onRemoveActivity={this.removeActivityPlan}
          />

          <Box sx={{ mb: 3 }}>
            <KpiPlanGridPanel
              rows={this.state.kpiPlanRows}
              candidates={this.state.indicatorCandidates}
              loading={this.state.loading || this.state.saving}
              editable={canPlanCurrentScope}
              allowAdd={canPlanCurrentScope}
              onRowsChange={(kpiPlanRows) => this.setState({ kpiPlanRows })}
              onSave={this.saveIndicators}
              onAdd={this.addIndicator}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                <ActivityTreePanel
                  activities={this.state.activities}
                  selectedNid={selected?.nid}
                  onSelect={(node) => void this.selectNode(node)}
                />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <ActivityFormPanel
                selectedNode={selected}
                loading={this.state.loading}
                saving={this.state.saving}
                planningCycleName={
                  this.props.plancontext?.pcn ??
                  this.props.planningContext?.pcn ??
                  ''
                }
                budgetPeriodName={this.state.budgetPeriodName}
                costCenterCode={model?.context.ccc ?? ''}
                activityCandidates={this.state.activityCandidates}
                selectedActivity={this.state.selectedActivityCandidate}
                activityWeight={this.state.activityWeight}
                driverValue={this.state.driverValue}
                targetValue={this.state.targetValue}
                activityDescription={this.state.activityDescription}
                onActivitySelect={(selectedActivityCandidate) =>
                  this.setState({ selectedActivityCandidate })
                }
                onFieldChange={(name, value) =>
                  this.setState({ [name]: value } as any)
                }
                addActivity={this.addActivity}
                updateActivity={this.updateActivity}
                canPlan={canPlanCurrentScope}
                taskRepository={this.state.taskRepository}
                taskRepositoryLoading={this.state.taskRepositoryLoading}
                selectedRepositoryTask={this.state.selectedRepositoryTask}
                onRepositoryTaskSelect={(selectedRepositoryTask) =>
                  this.setState({
                    selectedRepositoryTask,
                  })
                }
                onAddRepositoryTask={this.addRepositoryTaskToPlan}
                onCreateTask={this.openCreateTask}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 5 }}>
              {selected?.nodetype === 'ACTIVITY' ? (
                <Stack spacing={2}>
                  <ActivityTaskBudgetPanel
                    taskRows={this.state.taskRows}
                    budgetRows={this.state.budgetRows}
                    loading={this.state.loading}
                    canPlan={canPlanCurrentScope}
                    onPlanTasks={this.openTaskPlanning}
                  />
                  {/* <ActivityTaskBudgetPanel
                    taskRows={this.state.taskRows}
                    budgetRows={this.state.budgetRows}
                    accounts={this.state.accounts}
                    loading={this.state.loading}
                    onTaskRowsChange={(taskRows) => this.setState({ taskRows })}
                    onBudgetRowsChange={(budgetRows) =>
                      this.setState({ budgetRows })
                    }
                    onSaveTasks={async () => undefined}
                    onSaveBudgetLines={this.saveBudgetRows}
                  /> */}
                  {/* Temporary disable till mordernized */}
                  {/* <TemplateLibraryPanel
                    templates={this.state.templates}
                    applying={this.state.saving}
                    onApply={this.applyTemplate}
                  /> */}
                </Stack>
              ) : (
                <Alert severity="info">
                  Select an Activity to review its Tasks and prepare task-level
                  budget lines.
                </Alert>
              )}
            </Grid>
          </Grid>
        </Box>
        <CreateTaskDialog
          open={this.state.createTaskOpen}
          saving={this.state.saving}
          onClose={this.closeCreateTask}
          onCreate={this.createTask}
        />
        <TaskPlanningDialog
          open={this.state.taskPlanningOpen}
          activity={selectedActivity}
          taskRows={
            selectedActivity
              ? this.state.taskRows.filter(
                  (row: any) =>
                    Number(row.activityplanid) ===
                    Number(selectedActivity.activityplanid)
                )
              : []
          }
          accounts={this.state.accounts}
          planningCycleName={this.props.plancontext?.pcn ?? ''}
          costCentreName={this.state.model?.context?.ccn ?? ''}
          onClose={this.closeTaskPlanning}
          onSaved={this.onTaskPlanningSaved}
        />
      </PlanningWorkbenchShell>
    );
  }
}
